import * as React from "react";
import { useState, useEffect } from "react";
import styled from "styled-components";
import theme from "../../../util/theme";
import { mobileSize } from "../../../util/variables";

enum PointStates {
  NOT_COMPLETED,
  IN_PROGRESS,
  COMPLETED,
}

export const Point = ({
  startIdx,
  endIdx,
  currIdx,
  text,
  number,
  position,
  align,
}: {
  startIdx: number;
  endIdx: number;
  currIdx: number;
  text: string;
  number: number;
  position: number;
  align?: "left" | "right";
}) => {
  const { NOT_COMPLETED, IN_PROGRESS, COMPLETED } = PointStates;
  // NOT_COMPLETED: unfilled circle
  // IN_PROGRESS: filled green circle
  // COMPLETED: show number and text
  const [pointState, setPointState] = useState(NOT_COMPLETED);

  useEffect(() => {
    if (currIdx >= startIdx && currIdx <= endIdx) {
      setPointState(COMPLETED);
      return;
    }

    if (currIdx > startIdx) {
      setPointState(IN_PROGRESS);
      return;
    }

    setPointState(0);
  }, [startIdx, endIdx, currIdx]);

  return (
    <PointContainer position={position}>
      {pointState === COMPLETED && <PointText align={align}>{text}</PointText>}
      <Circle state={pointState}>{pointState === COMPLETED && number}</Circle>
    </PointContainer>
  );
};

const PointContainer = styled.div<{ position: number }>`
  position: absolute;
  left: ${props => props.position}%;
`;

const PointText = styled.p<{ align: "left" | "right" | undefined }>`
  position: absolute;
  top: 0px;
  left: 50%;

  font-size: 12px;
  text-transform: uppercase;
  white-space: nowrap;

  transform: ${props =>
    props.align === "left"
      ? "translate(-90%, -100%);"
      : props.align === "right"
        ? "translate(-9%, -100%)"
        : "translate(-50%, -100%)"};
`;

const Circle = styled.div<{ state: PointStates; mobileHideCircle: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  position: relative;
  left: 1px;

  width: ${props => (props.state === PointStates.COMPLETED ? "14px" : "6px")};
  height: ${props => (props.state === PointStates.COMPLETED ? "14px" : "6px")};
  border: ${props => (props.state === PointStates.NOT_COMPLETED ? "1px solid #8D8794" : "none")};
  border-radius: 50%;
  box-sizing: border-box;
  background-color: ${props =>
    props.state === PointStates.NOT_COMPLETED ? "transparent" : theme.DARK_GREEN_ACCENT};

  font-size: 10px;
  color: white;
  font-weight: 400;
`;

// ==PROGRESS BAR=========================================================

interface ProgressBarProps {
  childrenLength: number;
  currentIndex: number;
}

interface ProgressBarState {}

type Props = ProgressBarProps;

class OnboardingProgress extends React.Component<Props, ProgressBarState> {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(props: Props) {
    super(props);
  }

  render() {
    return (
      <ProgressBarDiv
        childrenLength={this.props.childrenLength}
        currentIndex={this.props.currentIndex}
      >
        <FillerDiv
          childrenLength={this.props.childrenLength}
          currentIndex={this.props.currentIndex}
        />
        <OverlayDiv>
          <Point
            startIdx={0}
            endIdx={3}
            currIdx={this.props.currentIndex}
            text="About"
            number={1}
            position={0}
            align="right"
          />
          <Point
            startIdx={4}
            endIdx={this.props.childrenLength - 2}
            currIdx={this.props.currentIndex}
            text="Interests"
            number={2}
            position={Math.floor((4 / (this.props.childrenLength - 1)) * 100)}
          />
          <Point
            startIdx={this.props.childrenLength - 1}
            endIdx={this.props.childrenLength - 1}
            currIdx={this.props.currentIndex}
            text="Your Plan"
            number={3}
            position={98}
            align="left"
          />
        </OverlayDiv>
      </ProgressBarDiv>
    );
  }
}

//= ===================
//= STYLED COMPONENTS
//= ===================

interface FillerProps {
  childrenLength: number;
  currentIndex: number;
}

export const ProgressBarDiv = styled.div<FillerProps>`
  position: relative;
  height: 10px;
  width: 50%;
  margin: 0px auto;
  margin-bottom: 98px;
  margin-top: 56px;
  border-radius: 50px;
  background-color: ${theme.LIGHT_GREY};
  z-index: 100;

  @media ${mobileSize} {
    margin: 36px auto 40px auto;
    width: 85%;
  }
`;

export const FillerDiv = styled.div<FillerProps>`
  background-color: ${theme.GREEN_ACCENT};
  height: inherit;
  border-radius: inherit;
  transition: width 0.2s ease-out;
  width: ${props => `${(props.currentIndex / (props.childrenLength - 1)) * 100}%`};
`;

export const OverlayDiv = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background-color: transparent;
  transition: width 0.2s ease-in;
`;

export default OnboardingProgress;
